/**
 * Standard API response models for user-management-lib
 * 
 * @remarks
 * The User Management API uses a specific response format:
 * - Success responses use: { status: "success", data: {...}, message?: string }
 * - Error responses use: { status: "error", message: string }
 * 
 * This differs from some other APIs that use { success: boolean }.
 * Keep these response structures intact to match the API specification exactly.
 */

/**
 * Generic API response wrapper
 * 
 * @remarks
 * All successful API responses follow this structure.
 * The data property type is generic and should be specified based on the endpoint.
 * 
 * @example
 * ```typescript
 * // Single user response
 * ApiResponse<{ user: User }>
 * 
 * // User list response
 * ApiResponse<{ users: User[] }>
 * ```
 */
export interface ApiResponse<T = any> {
  /**
   * Response status - "success" for successful operations
   */
  status: 'success' | 'error';

  /**
   * Response data payload
   * Structure varies by endpoint
   */
  data?: T;

  /**
   * Optional success or informational message
   */
  message?: string;
}

/**
 * API error response
 *
 * @deprecated Use {@link ApiErrorResponse} from 'cms-post-lib' instead.
 * Note: the backend `status: 'error'` field has been removed as of API v2.5.0.
 * Migrate callers to switch on `error.code` (ApiErrorCodeType) rather than
 * checking `error.status === 'error'`.
 *
 * @remarks
 * Returned when an API operation fails.
 * HTTP status codes indicate the error type:
 * - 400: Bad Request (validation errors)
 * - 401: Unauthorized (authentication required)
 * - 403: Forbidden (insufficient permissions)
 * - 404: Not Found (resource doesn't exist)
 * - 409: Conflict (duplicate resource)
 * - 500: Server Error
 */
export interface ApiError {
  /**
   * Error status - always "error"
   */
  status: 'error';

  /**
   * Error message describing what went wrong
   */
  message: string;

  /**
   * HTTP status code
   */
  statusCode?: number;

  /**
   * Validation errors by field name
   * Present for 400 Bad Request responses
   */
  errors?: Record<string, string[]>;
}

/**
 * Pagination metadata
 * 
 * @remarks
 * Included in list endpoints that support pagination.
 * Use these values to implement pagination UI controls.
 */
export interface PaginationMeta {
  /**
   * Current page number (1-indexed)
   */
  page: number;

  /**
   * Number of items per page
   */
  limit: number;

  /**
   * Total number of items across all pages
   */
  total: number;

  /**
   * Total number of pages
   */
  pages: number;

  /**
   * Whether there is a next page available
   */
  hasNext?: boolean;

  /**
   * Whether there is a previous page available
   */
  hasPrev?: boolean;
}

/**
 * Alternative pagination metadata format
 * 
 * @remarks
 * Some endpoints use this slightly different pagination structure.
 */
export interface PaginationMetaAlt {
  /**
   * Total number of items across all pages
   */
  total: number;

  /**
   * Current page number (1-indexed)
   */
  page: number;

  /**
   * Number of items per page
   */
  limit: number;

  /**
   * Total number of pages
   */
  totalPages: number;

  /**
   * Whether there are more pages available
   */
  hasMore?: boolean;
}

/**
 * Paginated API response
 * 
 * @remarks
 * Used for endpoints that return lists with pagination.
 * The data object contains both the items array and pagination metadata.
 * 
 * @example
 * ```typescript
 * // Users list with pagination
 * PaginatedResponse<User>
 * ```
 */
export interface PaginatedResponse<T> {
  /**
   * Response status
   */
  status: 'success';

  /**
   * Response data containing items and pagination
   */
  data: {
    /**
     * Array of items (property name varies by endpoint)
     */
    [key: string]: T[] | PaginationMeta | PaginationMetaAlt | any;

    /**
     * Pagination metadata
     */
    pagination: PaginationMeta | PaginationMetaAlt;
  };

  /**
   * Optional message
   */
  message?: string;
}

/**
 * Query parameters for list/search operations
 * 
 * @remarks
 * Common query parameters used across multiple endpoints.
 * Extend this interface for endpoint-specific parameters.
 */
export interface QueryParams {
  /**
   * Page number for pagination (default: 1)
   */
  page?: number;

  /**
   * Items per page (default varies by endpoint)
   */
  limit?: number;

  /**
   * Search query string
   */
  search?: string;

  /**
   * Additional dynamic parameters
   */
  [key: string]: string | number | boolean | string[] | undefined;
}
